import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTasks } from '../../../context/TasksContext';
import { daysBetween, relativeDay, shortDate } from '../../../utils/taskDates';
import ProgressCheck from '../../tasks/ProgressCheck';
import TaskIcon from '../../tasks/TaskIcon';
import { NodeBox } from './layout';
import { planChips } from '../planChips';
import ItemActions from '../ItemActions';
import { planClasses } from '../../../utils/projectPlan';

export type DropZone = 'before' | 'after' | 'child';
export type CommitNext = 'none' | 'sibling' | 'child';

interface MapNodeProps {
  box: NodeBox;
  /** Central topic only: the project's name and overall progress. */
  title?: string;
  rootProgress?: number;
  selected: boolean;
  /** Being typed into, starting from this text. */
  editing: string | null;
  flash: boolean;
  readOnly: boolean;
  hasChildren: boolean;
  collapsed: boolean;
  dropZone: DropZone | null;
  /** Being dragged: drawn this far from its place (stage units). */
  dragOffset: { x: number; y: number } | null;
  nodeRef: (el: HTMLDivElement | null) => void;
  onSelect: () => void;
  onStartEdit: () => void;
  onCommit: (text: string, next: CommitNext) => void;
  onToggleCollapse: () => void;
  onAddChild: () => void;
  onPointerDown: (e: React.PointerEvent) => void;
}

/**
 * One topic on the map. Ticks, people, notes and the ⋯ menu behave exactly as
 * they do on a list line (ProgressCheck, UserPicker, ItemMenu).
 */
const MapNode: React.FC<MapNodeProps> = (props) => {
  const { box, selected, editing, readOnly, hasChildren, collapsed } = props;
  const item = box.item;
  const { today, costs, updateItem, weeklyListId } = useTasks();

  const isRoot = !item;
  const done = !!item && item.progress >= 100;
  const style = {
    left: box.x,
    top: box.y,
    transform: props.dragOffset ? `translate(${props.dragOffset.x}px, ${props.dragOffset.y}px)` : undefined,
    '--tp-branch': `var(--tp-branch-${box.branch % 8})`,
  } as React.CSSProperties;

  const chips: React.ReactNode[] = [];
  if (item) {
    const overdue = !done && item.due_date && item.due_date < today;
    const soon = !done && item.due_date && !overdue && daysBetween(today, item.due_date) <= 1;
    if (item.due_date && !done) {
      chips.push(
        <span key="due" className={`tk-chip ${overdue ? 'tk-chip-danger' : soon ? 'tk-chip-warn' : ''}`} title={`Due ${shortDate(item.due_date)}`}>
          <TaskIcon name="due" size={11} />
          {overdue ? `Overdue · ${shortDate(item.due_date)}` : relativeDay(item.due_date, today)}
        </span>
      );
    }
    if (item.waiting_on && !done) {
      chips.push(
        <span key="wait" className="tk-chip tk-chip-muted" title={`Waiting on ${item.waiting_on}`}>
          <TaskIcon name="waiting" size={11} />
          {item.waiting_on}
        </span>
      );
    }
    chips.push(...planChips(item, costs));
    if (item.week_copy_list_id && item.week_copy_list_id === weeklyListId) {
      chips.push(
        <span key="week" className="tk-chip tk-chip-week" title="On this week's MPP list — changes show on both">
          <TaskIcon name="pin" size={11} />
          This week
        </span>
      );
    }
  }

  return (
    <div
      ref={props.nodeRef}
      className={[
        'tp-node',
        isRoot ? 'tp-node-root' : `tp-node-l${Math.min(box.level, 3)}`,
        selected ? 'is-selected' : '',
        done ? 'is-done' : '',
        props.flash ? 'is-flash' : '',
        props.dragOffset ? 'is-dragging' : '',
        props.dropZone ? `is-drop-${props.dropZone}` : '',
        box.side === -1 ? 'is-left' : '',
        item?.node_kind === 'material' ? 'is-material' : '',
        item ? planClasses(item) : '',
      ].join(' ')}
      style={style}
      data-node-key={box.key}
      onPointerDown={props.onPointerDown}
      onClick={(e) => {
        e.stopPropagation();
        props.onSelect();
      }}
      onDoubleClick={(e) => {
        e.stopPropagation();
        if (!readOnly && (e.currentTarget as HTMLElement).contains(e.target as Node)) props.onStartEdit();
      }}
    >
      {item && (
        <ProgressCheck
          value={item.progress}
          onToggle={() => !readOnly && updateItem(item.id, { progress: done ? 0 : 100 })}
          disabled={readOnly}
          label={`${done ? 'Uncheck' : 'Check'} ${item.text || 'topic'}`}
          size={18}
        />
      )}
      {isRoot && <ProgressCheck value={props.rootProgress || 0} label="Project progress" size={22} disabled />}

      <div className="tp-node-main">
        {editing !== null ? (
          <NodeEditor initial={editing} onCommit={props.onCommit} placeholder={isRoot ? 'Project name' : 'Topic'} />
        ) : (
          <span className={`tp-node-text${(isRoot ? props.title : item?.text) ? '' : ' is-empty'}`}>
            {(isRoot ? props.title : item?.text) || (isRoot ? 'Untitled project' : 'Topic')}
          </span>
        )}
        {chips.length > 0 && <div className="tp-node-meta">{chips}</div>}
      </div>

      {item && (
        <div className="tp-node-tail">
          <ItemActions item={item} readOnly={readOnly} hasChildren={hasChildren} onAddChild={props.onAddChild} />
        </div>
      )}

      {hasChildren && !isRoot && (
        <button
          type="button"
          className={`tp-fold${collapsed ? ' is-collapsed' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            props.onToggleCollapse();
          }}
          title={collapsed ? `Show ${box.hiddenCount} hidden` : 'Fold this branch'}
          aria-label={collapsed ? 'Expand branch' : 'Collapse branch'}
        >
          {collapsed ? box.hiddenCount : '−'}
        </button>
      )}

    </div>
  );
};

/** Inline text box: Enter adds a sibling, Tab a sub-topic, Esc / click away just saves. */
const NodeEditor: React.FC<{ initial: string; placeholder: string; onCommit: (text: string, next: CommitNext) => void }> = ({
  initial,
  placeholder,
  onCommit,
}) => {
  const [draft, setDraft] = useState(initial);
  const ref = useRef<HTMLTextAreaElement>(null);
  const committed = useRef(false);
  const commit = (next: CommitNext) => {
    if (committed.current) return;
    committed.current = true;
    onCommit(draft, next);
  };

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [draft]);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, []);

  return (
    <textarea
      ref={ref}
      className="tp-node-input"
      rows={1}
      value={draft}
      placeholder={placeholder}
      onChange={(e) => setDraft(e.target.value.replace(/\n/g, ' '))}
      onBlur={() => commit('none')}
      onPointerDown={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          commit('sibling');
        } else if (e.key === 'Tab') {
          e.preventDefault();
          commit('child');
        } else if (e.key === 'Escape') {
          e.preventDefault();
          commit('none');
        }
      }}
      aria-label="Topic text"
    />
  );
};

export default MapNode;
